import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Users, Target, Star, Award } from 'lucide-react';
import { analyticsService, UnifiedUser } from '@/services/analyticsService';

interface PlayerStat {
  name: string;
  points: number;
  assists: number;
  goals: number;
  games: number;
  avg_points: number;
}

interface TopPerformer {
  name: string;
  value: number;
  metric: string;
}

const CHART_COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const PlayerAnalytics = () => {
  const [playerStats, setPlayerStats] = useState<PlayerStat[]>([]);
  const [topPerformers, setTopPerformers] = useState<TopPerformer[]>([]);
  const [selectedMetric, setSelectedMetric] = useState('points');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPlayerAnalytics();
  }, []);

  const fetchPlayerAnalytics = async () => {
    try {
      const [playerData, userData] = await Promise.all([
        analyticsService.getPlayerAnalytics(),
        analyticsService.getUnifiedUsers()
      ]);

      if (!playerData || playerData.length === 0) {
        setPlayerStats([]);
        setTopPerformers([]);
        return;
      }

      const userMap = new Map<string, UnifiedUser>();
      userData.forEach(user => userMap.set(user.id, user));

      const processedStats = playerData.map((player: any) => {
        const user = userMap.get(player.player_id);
        return {
          name: user?.display_name || `Player ${player.player_id.substring(0, 8)}`,
          points: player.total_points || 0,
          assists: player.total_assists || 0,
          goals: player.total_goals || 0,
          games: player.games_played || 0,
          avg_points: player.avg_points || 0
        };
      });

      const processedTopPerformers = playerData
        .sort((a: any, b: any) => (b.total_points || 0) - (a.total_points || 0))
        .slice(0, 3)
        .map((player: any) => {
          const user = userMap.get(player.player_id);
          return {
            name: user?.display_name || `Player ${player.player_id.substring(0, 8)}`,
            value: player.total_points || 0,
            metric: 'Total Points'
          };
        });

      setPlayerStats(processedStats);
      setTopPerformers(processedTopPerformers);
    } catch (error) {
      console.error('Error in fetchPlayerAnalytics:', error);
      setPlayerStats([]);
      setTopPerformers([]);
    } finally {
      setLoading(false);
    }
  };

  // Removed mock data - using only real data

  const getChartData = () => {
    return playerStats.map(player => ({
      name: player.name,
      value: player[selectedMetric as keyof PlayerStat] as number
    }));
  };

  const getMetricLabel = (metric: string) => {
    const labels: Record<string, string> = {
      points: 'Points',
      assists: 'Assists', 
      goals: 'Goals',
      avg_points: 'Avg Points'
    };
    return labels[metric] || metric;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        {[1, 2, 3].map(i => (
          <Card key={i}>
            <CardContent className="p-6">
              <div className="animate-pulse space-y-4">
                <div className="h-6 bg-muted rounded w-1/3"></div>
                <div className="h-32 bg-muted rounded"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Top Performers */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {topPerformers.map((performer, index) => (
          <Card key={index}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{performer.metric}</p>
                  <p className="text-lg font-bold">{performer.name}</p>
                  <p className="text-2xl font-bold text-primary">{performer.value}</p>
                </div>
                <Award className="h-8 w-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
        ))}
        {topPerformers.length === 0 && (
          <Card className="col-span-3">
            <CardContent className="p-8 text-center">
              <p className="text-muted-foreground">No player data available yet</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Player Performance Chart */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Player Performance Comparison</CardTitle>
            <Select value={selectedMetric} onValueChange={setSelectedMetric}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="points">Points</SelectItem>
                <SelectItem value="assists">Assists</SelectItem>
                <SelectItem value="goals">Goals</SelectItem>
                <SelectItem value="avg_points">Avg Points</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {playerStats.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={getChartData()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center text-muted-foreground py-8">No player performance data available</p>
          )}
        </CardContent>
      </Card>

      {/* Top Performers Distribution */}
      <Card>
        <CardHeader>
          <CardTitle>Top Performers Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          {topPerformers.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={topPerformers}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label
                  >
                    {topPerformers.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-4">
                <h4 className="font-semibold">Performance Insights</h4>
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">
                    Total Active Players: {playerStats.length}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Average {getMetricLabel(selectedMetric)}: {
                      playerStats.length > 0 
                        ? Math.round(playerStats.reduce((sum, player) => sum + (player[selectedMetric as keyof PlayerStat] as number), 0) / playerStats.length)
                        : 0
                    }
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Top Performer: {topPerformers[0]?.name || 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">No performance distribution data available</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PlayerAnalytics;